import { useAdminGetMe } from '@/api/generated/v2-admin/v2-admin'
import type { AdminInfoResponse } from '@/api/schemas'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { adminApi, adminAuth, adminLoginRedirectSearch } from '@/lib/adminApi'
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'

// 외부 앱(Claude Code 등) OAuth 관리자 접근 승인 화면 — agent_docs/mcp_server.md §13-2.
// 백엔드 authorize 가 `?intent=<핸들>&client_name=<앱 이름>&redirect_host=<돌아갈 호스트>` 로 보낸다.
// 미로그인이면 /admin 래퍼가 쿼리까지 redirect 로 넘겨 로그인 후 이 URL 로 돌아온다.
// ⚠️ bind 는 반드시 "관리자로 계속" 버튼 클릭으로만 한다(자동 POST 금지 — 검토 W1).
export const Route = createFileRoute('/admin/oauth-approve')({
  component: AdminOAuthApprove,
})

/** intent 핸들 최대 길이 — 이보다 길면 위조·잘림으로 보고 요청을 보내지 않는다 */
const MAX_INTENT_LENGTH = 128

/** 실패 시 공통 안내 — intent 는 1회용이라 이 화면에서 재시도할 수 없다 */
const RETRY_FROM_APP = '앱에서 처음부터 다시 시도해 주세요.'

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'redirecting' }
  | { kind: 'cancelled' }
  | { kind: 'error'; message: string }

function AdminOAuthApprove() {
  const navigate = useNavigate()
  const location = useLocation()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  // 쿼리는 원문 그대로 읽는다 — 라우터 기본 parseSearch 는 숫자·true 처럼 보이는 값을
  // JSON 으로 바꿔 버려 핸들이 변형될 수 있다
  const { intent, clientName, redirectHost } = useMemo(() => {
    const params = new URLSearchParams(location.searchStr)
    return {
      intent: params.get('intent') ?? '',
      clientName: params.get('client_name')?.trim() || null,
      redirectHost: params.get('redirect_host')?.trim() || null,
    }
  }, [location.searchStr])
  const intentValid = intent.length > 0 && intent.length <= MAX_INTENT_LENGTH

  // 지금 로그인한 관리자 — 다른 계정으로 승인하는 실수를 알아보게 보여 준다
  const {
    data: meRaw,
    error: meError,
    refetch: refetchMe,
    isFetching: meFetching,
  } = useAdminGetMe()
  const adminInfo = (meRaw ?? null) as AdminInfoResponse | null
  // custom-instance 는 실패를 `{ status, ...body }` 로 던진다. 네트워크 오류는 status 가 없다
  const meStatus = (meError as { status?: number } | null)?.status
  const meUnauthorized = meStatus === 401
  // 401 이 아닌 실패(네트워크·5xx) — 토큰은 그대로 두고 다시 시도하게 한다
  const meFailed = !!meError && !meUnauthorized

  // 다시 로그인 — 토큰을 지우고 이 화면(쿼리 포함)을 redirect 로 넘긴다
  const goLogin = () => {
    adminAuth.removeToken()
    navigate({
      to: '/admin/login',
      search: adminLoginRedirectSearch(location.pathname + location.searchStr),
      replace: true,
    })
  }

  // 토큰이 만료·폐기돼 관리자 조회가 401 이면 다시 로그인. 그 밖의 실패로 토큰을 지우면
  // 일시 장애에도 로그인부터 다시 하게 되므로 401 일 때만 지운다
  // goLogin 은 렌더마다 새로 만들어지므로 의존성에서 뺀다 — 401 이 될 때 한 번만 돈다
  useEffect(() => {
    if (meUnauthorized) goLogin()
  }, [meUnauthorized])

  const handleContinue = async () => {
    if (!intentValid || phase.kind !== 'idle') return
    setPhase({ kind: 'submitting' })
    const result = await adminApi.bindOAuthIntent(intent)
    if (result.ok) {
      setPhase({ kind: 'redirecting' })
      // 동의 화면은 api 도메인에 있다 — 바인딩 쿠키가 실리도록 전체 이동한다
      window.location.assign(adminApi.oauthNextUrl(result.next))
      return
    }
    if (result.kind === 'unauthorized') {
      goLogin()
      return
    }
    setPhase({ kind: 'error', message: result.message ?? RETRY_FROM_APP })
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center py-8">
      <div className="max-w-md w-full mx-4">
        <Card className="p-6">
          <h1 className="text-xl font-bold text-on-surface">
            외부 앱 연결 — 관리자 접근
          </h1>

          {!intentValid ? (
            // 핸들이 없거나 비정상 — 서버 호출 없이 안내만
            <p className="mt-4 text-sm text-on-surface-variant">
              연결 요청 정보가 올바르지 않아요. {RETRY_FROM_APP}
            </p>
          ) : phase.kind === 'cancelled' ? (
            <p className="mt-4 text-sm text-on-surface-variant">
              연결을 취소했어요. 이 창을 닫고, 앱에서도 연결을 취소해 주세요.
            </p>
          ) : (
            <>
              {/* 표시용 값은 서버가 실어 보낸 것 — 텍스트로만 그리고 긴 값은 줄바꿈한다 */}
              <dl className="mt-5 space-y-3 text-sm">
                <div>
                  <dt className="text-on-surface-variant">
                    앱 이름 <span className="text-xs">(앱이 밝힌 값)</span>
                  </dt>
                  <dd className="mt-0.5 font-medium text-on-surface break-all">
                    {clientName ?? '(알 수 없음)'}
                  </dd>
                </div>
                <div>
                  <dt className="text-on-surface-variant">
                    돌아갈 곳 <span className="text-xs">(앱이 밝힌 값)</span>
                  </dt>
                  <dd className="mt-0.5 font-medium text-on-surface break-all">
                    {redirectHost ?? '(알 수 없음)'}
                  </dd>
                </div>
                <div>
                  <dt className="text-on-surface-variant">로그인한 관리자</dt>
                  <dd className="mt-0.5 font-medium text-on-surface break-all">
                    {adminInfo?.admin_email ??
                      (meFailed ? '확인하지 못했어요' : '확인 중...')}
                  </dd>
                </div>
              </dl>
              {/* 앱 이름·돌아갈 곳은 쿼리로 실려 온 표시용 값이라 위조될 수 있다 — 확인된 값은 동의 화면이 다시 보인다 */}
              <p className="mt-2 text-xs text-on-surface-variant">
                정확한 앱 정보는 다음 화면에서 다시 확인돼요.
              </p>

              <p className="mt-5 text-sm text-on-surface bg-surface-low p-3 rounded-md">
                이 앱이 다음 화면에서 고른 실험실 매장의 매출·광고·상태 자료를
                읽어요. 수강생 본인 연결과 별개예요.
              </p>

              {/* 관리자 조회 일시 실패 — 승인 전에 누구로 묶이는지 보여야 하므로 다시 불러오게 한다 */}
              {meFailed && !adminInfo && (
                <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-sm bg-error/10 p-3 rounded-md">
                  <p className="text-error" role="alert">
                    관리자 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => refetchMe()}
                    disabled={meFetching}
                    className="shrink-0"
                  >
                    {meFetching ? '다시 불러오는 중...' : '다시 시도'}
                  </Button>
                </div>
              )}

              {phase.kind === 'error' && (
                <div
                  className="mt-4 text-error text-sm bg-error/10 p-3 rounded-md"
                  role="alert"
                >
                  {phase.message}
                </div>
              )}

              {/* 실패한 intent 는 이미 소비됐을 수 있어 다시 누를 수 없게 버튼을 감춘다 */}
              {phase.kind !== 'error' && (
                <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setPhase({ kind: 'cancelled' })}
                    disabled={phase.kind !== 'idle'}
                  >
                    취소
                  </Button>
                  <Button
                    onClick={handleContinue}
                    loading={
                      phase.kind === 'submitting' ||
                      phase.kind === 'redirecting'
                    }
                    disabled={phase.kind !== 'idle' || !adminInfo}
                  >
                    {phase.kind === 'redirecting'
                      ? '이동 중...'
                      : '관리자로 계속'}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  )
}
